/** 链上交易相关合约地址（通过环境变量配置） */

/** 平台账本 / 交易余额查询币种（接口 currency；UI 展示为 USD） */
export const PLATFORM_WALLET_CURRENCY = 'DUSD';

export function getExchangeContractAddress() {
  return String(process.env.VUE_APP_NEG_RISK_CTF_EXCHANGE ?? '').trim();
}

/** 官方 USDC（充值 transfer 到 Cobo） */
export function getUsdcContractAddress() {
  return String(process.env.VUE_APP_USDC_CONTRACT ?? '').trim();
}

/** 交易本位 dUSD（approve / 余额） */
export function getDusdContractAddress() {
  return String(process.env.VUE_APP_DUSD_CONTRACT ?? '').trim();
}

/** Polygon 桥接 USDC.e，MetaMask 里常显示为 USDC */
export const POLYGON_USDC_BRIDGED = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';

/** 查询余额时需覆盖的 USDC 合约（原生 + 桥接，去重） */
export function getUsdcContractAddresses() {
  const native = getUsdcContractAddress();
  const bridged = String(process.env.VUE_APP_USDC_BRIDGED_CONTRACT ?? POLYGON_USDC_BRIDGED).trim();
  return [...new Set([native, bridged].filter(Boolean))];
}

export function getConditionalTokensContractAddress() {
  return String(process.env.VUE_APP_CONDITIONAL_TOKENS_CONTRACT ?? '').trim();
}

export function getCtfExchangeContractAddress() {
  return String(process.env.VUE_APP_CTF_EXCHANGE ?? '').trim();
}

/** 现行 NegRisk Adapter（approve / 赎回）；缺省 = TEST Go neg_risk_adapter */
export function getNegRiskAdapterContractAddress() {
  return String(
    process.env.VUE_APP_NEG_RISK_ADAPTER ?? '0x5d1a9d4445DacfC60D067c43A9CAe024085c144e'
  ).trim();
}

/** dUSD 世代 Adapter（仅赎回）；缺省 = Go legacy_neg_risk_adapter */
export function getLegacyNegRiskAdapterContractAddress() {
  return String(
    process.env.VUE_APP_LEGACY_NEG_RISK_ADAPTER ?? '0xaCB0dFE9aFC8338081bC2b3f64b1255d82d78b94'
  ).trim();
}

/** dUSD 世代 Exchange（仅赎回 / 禁止当现行 approve spender） */
export function getLegacyExchangeContractAddress() {
  return String(
    process.env.VUE_APP_LEGACY_NEG_RISK_CTF_EXCHANGE ?? '0xC13Df9a7798F990fdf7ff04B13Cff463bEfa289B'
  ).trim();
}

/** 钱包提现 Offramp.unwrapTo */
export function getCollateralOfframpAddress() {
  return String(
    process.env.VUE_APP_COLLATERAL_OFFRAMP ?? '0x7DADAf9b24c64b58C565c599d9C557aFA3374712'
  ).trim();
}

/** 费用模块（买单/卖单 approve spender）；缺省 = TEST Go neg_risk_fee_module */
export function getNegRiskFeeModuleContractAddress() {
  return String(
    process.env.VUE_APP_NEG_RISK_FEE_MODULE ?? '0xDe45a266F0F78b74ad1D6053DEE40BeF855fe5C6'
  ).trim();
}

export function getNegRiskVaultContractAddress() {
  return String(process.env.VUE_APP_NEG_RISK_VAULT ?? '').trim();
}

function isHexAddress(value) {
  return /^0x[a-fA-F0-9]{40}$/.test(String(value ?? '').trim());
}

/**
 * 从接口 / 市场字段解析 Exchange 地址（EIP-712 verifyingContract）。
 * 优先 exchange* / verifyingContract；不用 collateral（抵押品代币地址）。
 */
export function pickExchangeAddressFromSources(...sources) {
  for (const source of sources) {
    if (source == null) continue;
    if (typeof source === 'string' || typeof source === 'number') {
      const raw = String(source).trim();
      if (isHexAddress(raw)) return raw;
      continue;
    }
    if (typeof source !== 'object') continue;
    const candidates = [
      source.verifyingContract,
      source.exchangeAddress,
      source.exchange_address,
      source.exchange,
      source.ctfExchangeAddress,
      source.ctf_exchange_address,
      source.negRiskExchangeAddress,
      source.neg_risk_exchange_address,
    ];
    for (const value of candidates) {
      const raw = String(value ?? '').trim();
      if (isHexAddress(raw)) return raw;
    }
  }
  return '';
}

/** 市场抵押品：DUSD 可交易；USDC 仅赎回 */
export function pickTradingCollateral(source) {
  if (source == null) return '';
  if (typeof source === 'string') return String(source).trim().toUpperCase();
  if (typeof source !== 'object') return '';
  const raw =
    source.tradingCollateral ??
    source.trading_collateral ??
    source.collateralType ??
    source.collateral_type ??
    source.collateralSymbol ??
    source.collateral_symbol ??
    '';
  return String(raw).trim().toUpperCase();
}

export function isDusdTradingCollateral(source) {
  const c = pickTradingCollateral(source);
  return !c || c === 'DUSD' || c === 'USD';
}

export function isUsdcOnlyRedeemCollateral(source) {
  return pickTradingCollateral(source) === 'USDC';
}

/** 列表/详情：一组 market（或已映射行）是否全部为 USDC 仅赎回 */
export function areSourcesAllUsdcOnlyRedeem(sources) {
  const list = (Array.isArray(sources) ? sources : []).filter(Boolean);
  if (!list.length) return false;
  return list.every((item) => isUsdcOnlyRedeemCollateral(item));
}

/** 是否存在可交易的 DUSD（空抵押按 DUSD） */
export function areSourcesHaveTradableDusd(sources) {
  const list = (Array.isArray(sources) ? sources : []).filter(Boolean);
  if (!list.length) return false;
  return list.some((item) => isDusdTradingCollateral(item));
}

/** 二元市场 → NegRiskCtfExchange；多元市场 → CTFExchange */
export function resolveApprovalExchange(marketKind = 'binary') {
  const isMulti = marketKind === 'multipleChoice';
  const exchange = isMulti ? getCtfExchangeContractAddress() : getExchangeContractAddress();
  if (!exchange) {
    throw new Error(
      isMulti
        ? 'VUE_APP_CTF_EXCHANGE not configured'
        : 'VUE_APP_NEG_RISK_CTF_EXCHANGE not configured'
    );
  }
  return exchange;
}

/**
 * EIP-712 verifyingContract：接口/市场字段优先，否则按 marketKind 回落到 env。
 */
export function resolveVerifyingContractAddress(sources = {}, marketKind = 'binary') {
  const fromApi = pickExchangeAddressFromSources(sources);
  if (fromApi) return fromApi;
  return resolveApprovalExchange(marketKind);
}

/**
 * 买卖单 dUSD.approve 的 spender：Exchange + FeeModule。
 * 二元 → NegRiskCtfExchange；多元 → CTFExchange。
 */
export function resolveDusdApprovalSpenders(marketKind = 'binary') {
  const exchange = resolveApprovalExchange(marketKind);
  const feeModule = getNegRiskFeeModuleContractAddress();
  if (!feeModule) throw new Error('VUE_APP_NEG_RISK_FEE_MODULE not configured');
  return [...new Set([exchange, feeModule])];
}

/** @deprecated 使用 resolveDusdApprovalSpenders；保留以免外部误用旧 Adapter 口径 */
export function resolveBuyApprovalSpender(marketKind = 'binary') {
  return resolveDusdApprovalSpenders(marketKind)[0];
}

/** 卖单 CT.setApprovalForAll 的 operator：NegRiskCtfExchange + NegRiskAdapter */
export function resolveSellApprovalOperators() {
  const exchange = getExchangeContractAddress();
  if (!exchange) throw new Error('VUE_APP_NEG_RISK_CTF_EXCHANGE not configured');
  const adapter = getNegRiskAdapterContractAddress();
  if (!adapter) throw new Error('VUE_APP_NEG_RISK_ADAPTER not configured');
  return [...new Set([exchange, adapter])];
}

export function assertTradeContractsConfigured(side, marketKind = 'binary') {
  const exchange = resolveApprovalExchange(marketKind);
  const normalizedSide = String(side ?? 'BUY')
    .trim()
    .toUpperCase();

  const dusd = getDusdContractAddress();
  if (!dusd) throw new Error('VUE_APP_DUSD_CONTRACT not configured');
  resolveDusdApprovalSpenders(marketKind);

  if (normalizedSide === 'SELL') {
    const ct = getConditionalTokensContractAddress();
    if (!ct) throw new Error('VUE_APP_CONDITIONAL_TOKENS_CONTRACT not configured');
    resolveSellApprovalOperators(marketKind);
  }

  return {
    exchange,
    dusd,
    usdc: getUsdcContractAddress(),
    adapter: getNegRiskAdapterContractAddress(),
    feeModule: getNegRiskFeeModuleContractAddress(),
    conditionalTokens: getConditionalTokensContractAddress(),
    marketKind,
  };
}
